import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaUserCircle,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { RiDashboardFill } from 'react-icons/ri';
import { AiOutlineAppstore } from 'react-icons/ai';
import { RiLockPasswordLine } from 'react-icons/ri';

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const [openSections, setOpenSections] = useState({
    masters: false,
    orders: false,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Scroll to top function
const scrollToTop = () => {
  const main = document.querySelector('main');
  if (main) {
    main.scrollTo({ top: 0, behavior: 'smooth' });
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
  // Auto scroll when route changes
  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);

  // Auth + multi-tab sync
  useEffect(() => {
    const checkAuthentication = () => {
      const encodedAdminId = sessionStorage.getItem('admin_id_encoded');
      if (!encodedAdminId) {
        navigate('/admin-login', { replace: true });
        return;
      }
      try {
        const decoded = atob(encodedAdminId);
        if (!decoded || decoded.trim() === '') {
          sessionStorage.removeItem('admin_id_encoded');
          navigate('/admin-login', { replace: true });
        }
      } catch (err) {
        console.warn('Invalid admin_id_encoded format:', err);
        sessionStorage.removeItem('admin_id_encoded');
        navigate('/admin-login', { replace: true });
      }
    };

    checkAuthentication();

    const handleStorageChange = (e) => {
      if (e.key === 'admin_id_encoded' && !e.newValue) {
        navigate('/admin-login', { replace: true });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_id_encoded');
    toast.success("Logged out successfully");
    navigate('/admin-login', { replace: true });
    window.dispatchEvent(new Event('storage'));
  };

  const menuItems = [
    {
      type: 'single',
      key: 'dashboard',
      icon: <RiDashboardFill />,
      label: 'Dashboard',
    },
    {
      type: 'group',
      title: 'Masters',
      sectionKey: 'masters',
      icon: <AiOutlineAppstore />,
      subMenus: [
        {
          title: 'Masters Management',
          items: [
            { key: 'all-category',    icon: <AiOutlineAppstore />, label: 'All Category'    },
            // { key: 'all-subCategory', icon: <AiOutlineAppstore />, label: 'All Sub Category' },
            { key: 'all-products',    icon: <AiOutlineAppstore />, label: 'All Products'    },
            { key: 'all-brand',       icon: <AiOutlineAppstore />, label: 'All Brand'       },
            { key: 'all-banner',      icon: <AiOutlineAppstore />, label: 'All Banner'      },
            { key: 'all-customers',   icon: <AiOutlineAppstore />, label: 'All Customers'   },
          ],
        },
      ],
    },
    {
      type: 'group',
      title: 'Orders',
      sectionKey: 'orders',
      icon: <AiOutlineAppstore />,
      subMenus: [
        {
          title: 'Order Reports',
          items: [
            { key: 'all-orders',     icon: <AiOutlineAppstore />, label: 'All Orders'     },
            { key: 'today-orders',   icon: <AiOutlineAppstore />, label: 'Today Orders'   },
            { key: 'weekly-orders',  icon: <AiOutlineAppstore />, label: 'Weekly Orders'  },
            { key: 'monthly-orders', icon: <AiOutlineAppstore />, label: 'Monthly Orders' },
          ],
        },
      ],
    },
    {
      type: 'single',
      key: 'change-password',
      icon: <RiLockPasswordLine />,
      label: 'Change Password',
    },
  ];

  const currentPath = location.pathname === '/admin-panel' ? 'dashboard' : location.pathname.replace('/admin-panel/', '');
  const isActive = (key) => key === currentPath;

  useEffect(() => {
    const mastersRoutes = ['all-category', 'all-subCategory', 'all-products', 'all-brand', 'all-banner', 'all-customers'];
    const ordersRoutes  = ['all-orders', 'today-orders', 'weekly-orders', 'monthly-orders'];

    setOpenSections({
      masters: mastersRoutes.includes(currentPath),
      orders: ordersRoutes.includes(currentPath),
    });
  }, [currentPath]);

  const PRIMARY = '#f36c32';

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'w-72' : 'w-16'
        } bg-gray-900 text-white border-r border-gray-800 shadow-xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {sidebarOpen && (
          <div className="p-6 border-b border-gray-800 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#f36c32]/40 flex-shrink-0">
              <div className="w-full h-full bg-white flex items-center justify-center">
                <FaUserCircle className="text-3xl text-[#f36c32]" />
              </div>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-base truncate">AHA MART</p>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-3 pt-4 pb-6 space-y-1">
          {menuItems.map((menu, idx) => {
            if (menu.type === 'single') {
              const active = isActive(menu.key);
              return (
                <button
                  key={menu.key || `single-${idx}`}
                  onClick={() => {
                    navigate(`/admin-panel/${menu.key}`);
                    scrollToTop();
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center ${
                    sidebarOpen ? 'justify-start gap-4 px-5' : 'justify-center px-3'
                  } py-3 rounded-lg transition-all duration-200 text-sm font-medium ${
                    active
                      ? `bg-[#f36c32] text-white shadow-md`
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  title={!sidebarOpen ? menu.label : undefined}
                >
                  <div className={`text-2xl flex-shrink-0 ${active ? 'text-white' : 'text-[#f36c32]'}`}>
                    {menu.icon}
                  </div>
                  {sidebarOpen && <span className="truncate">{menu.label}</span>}
                </button>
              );
            }

            if (menu.type === 'group') {
              const isSectionOpen = openSections[menu.sectionKey];
              const isActiveGroup = menu.subMenus?.some((sub) =>
                sub.items.some((item) => isActive(item.key))
              );

              return (
                <div key={menu.title} className="mb-1">
                  <button
                    onClick={() => toggleSection(menu.sectionKey)}
                    className={`w-full flex items-center justify-between px-5 py-3 rounded-lg transition-all text-sm font-semibold ${
                      isActiveGroup
                        ? `bg-gray-800 text-[#f36c32] border-l-4 border-[#f36c32]`
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-2xl">{menu.icon}</div>
                      {sidebarOpen && <span>{menu.title}</span>}
                    </div>
                    {sidebarOpen && (isSectionOpen ? <FaChevronUp /> : <FaChevronDown />)}
                  </button>

                  {isSectionOpen && sidebarOpen && (
                    <div className="pl-6 pr-2 mt-1 space-y-1">
                      {menu.subMenus.map((sub) => (
                        <div key={sub.title}>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            {sub.title}
                          </div>
                          {sub.items.map((item) => {
                            const active = isActive(item.key);
                            return (
                              <button
                                key={item.key}
                                onClick={() => {
                                  navigate(`/admin-panel/${item.key}`);
                                  scrollToTop();
                                  if (window.innerWidth < 1024) setSidebarOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-5 py-2.5 rounded-lg transition-all text-sm ${
                                  active
                                    ? `bg-[#f36c32]/20 text-[#f36c32] font-semibold`
                                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                                }`}
                              >
                                <div className="text-xl">{item.icon}</div>
                                <span className="truncate">{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>

        <div className="border-t border-gray-800 mt-auto">
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className={`w-full flex items-center ${
              sidebarOpen ? 'justify-start gap-4 px-5 py-4' : 'justify-center py-4 px-3'
            } text-gray-300 hover:bg-gray-800 hover:text-[#f36c32] transition-colors text-base`}
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <FaSignOutAlt className="text-2xl flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        sidebarOpen ? 'lg:ml-72' : 'lg:ml-16'
      }`}>
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="px-5 sm:px-6 lg:px-8 py-4 flex items-center justify-between h-16 relative">
            <button
              className={`text-3xl p-2 rounded-lg hover:bg-gray-100 transition-colors ${sidebarOpen ? 'text-[#f36c32]' : 'text-gray-600'}`}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              {sidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}
            </button>

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <h1 
                className="text-2xl sm:text-3xl font-bold tracking-tight"
                style={{ color: PRIMARY }}
              >
                AHA MART
              </h1>
            </div>

            <div className="flex items-center gap-6 sm:gap-8" />
          </div>
        </header>

        <main className="flex-1 p-6 md:p-8 lg:p-10 bg-gray-50 overflow-auto">
          <div className="max-w-[1600px] mx-auto bg-white rounded-xl shadow-sm p-6 md:p-10">
            <Outlet />
          </div>
        </main>
      </div>

      {sidebarOpen && window.innerWidth < 1024 && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-sm">
            <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">Confirm Logout</h2>
            <p className="text-gray-600 mb-8 text-center text-base">
              Are you sure you want to logout?
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-4 px-6 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition text-base font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLogoutConfirm(false);
                  handleLogout();
                }}
                className="flex-1 py-4 px-6 bg-[#f36c32] text-white rounded-lg hover:bg-orange-600 transition text-base font-medium"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;